import { ATMsStrikeMenuItemType } from '../pages/home'
import { CustomOptionSecurityType, futureTradeDataType, optionsTradesDataType, optionTradeDataType } from '../pages/typings/options.typings'
import { FutureProto, GetOptionChainRequestV2, Outlook } from '../proto/generated/candle-data-depot-option-chain'
import { OptionType, Security, SecurityCategory_CategoryCode } from '../proto/generated/security'
import { getOptionChainV2, getOptionExpiriesV2 } from '../services/apis/optionChain'
import { getSecurityKey } from './security'
import { Expiry, TimeBucket, TimeBucket_TimeSeries } from '../proto/generated/common'
import { shortMonthNames } from './general'
import { AnyAction, Dispatch } from 'redux'
import { DropDownExpiries, OptionsStateType, setDropdownOptionExpiries, setOptionChainDataFunction } from '../reducer/options.reducer'
import { setFutureExpiriesData } from '../reducer/futures.reducer'
import { calculateOiRanks } from './tradePage'
import { calculateDifferenceInDays } from './strategyBuilder'
import { Strike } from '../pages/typings/options.typings'
import { setOptionChainExpandedRows } from '../reducer/home.reducer'
import { getHistoricalOptionChain, GetHistoricOptionChainRequest } from '../services/apis/simulator'
import { OptionChainViewKey } from '../assets/constants'
import { OrdersDataType } from '../reducer/orders.reducer'
import store from '../store'
import { Snapshot } from '../reducer/security.reducer'
export function findClosestSubarray(
    arr: optionTradeDataType[],
    k: number,
    n: number
) {
    if (arr.length < n) {
        return []
    }
    let minSum = 0
    for (let i = 0; i < n; i++) {
        minSum += Math.abs(arr[i].strikeCol.strike - k)
    }
    let left = 0
    let minLeft = 0

    for (let i = n; i < arr.length; i++) {
        const sum =
            minSum -
            Math.abs(arr[left].strikeCol.strike - k) +
            Math.abs(arr[i].strikeCol.strike - k)
        if (sum <= minSum) {
            minSum = sum
            minLeft = left + 1
        }

        left++
    }
    return arr.slice(minLeft, minLeft + n)
}

export function convertDateToShortEnglishFormat(dateString: string, capitalized: boolean = false) {
    const year = Number(dateString.substring(0, 4))
    const month = Number(dateString.substring(4, 6))
    const day = Number(dateString.substring(6, 8))
    const date = new Date(year, month - 1, day)
    const shortMonthName = shortMonthNames[date.getMonth()]
    if (capitalized) {
        const formattedDate = `${day < 10 ? '0' + day : day}${shortMonthName}${String(year).slice(2)}`
        return formattedDate.toUpperCase()
    }
    const formattedDate = `${shortMonthName} ${day}, ${year}`
    return formattedDate
}

function getExpiryObjectFromTimestamp(timestamp: number, weekly: boolean, referenceTime?: number): Expiry {
    const msInDay = 24 * 60 * 60 * 1000; // Milliseconds in a day
    const currentDate: any = referenceTime ? new Date(referenceTime) : new Date();
    const targetDate: any = new Date(timestamp);

    const numberOfDaysToExpiry = Math.max(
        Math.ceil((targetDate - currentDate) / msInDay),
        0
    )
    const yyyymmdd = `${targetDate.getFullYear()}${String(targetDate.getMonth() + 1).padStart(2, '0')}${String(targetDate.getDate()).padStart(2, '0')}`;

    const pinData = {
        date: yyyymmdd,
        expiry: timestamp,
        numberOfDaysToExpiry,
        weeklyExpiry: weekly,
        name: targetDate.toLocaleDateString("en-US", { day: "2-digit", month: "short" }),
    };

    return pinData;
}

export const ivColors: Record<string, string> = {
    'ATM (CALL)': '#ffe102',
    'ATM (PUT)': '#0004ff',
    'ATM + 1 (CALL)': '#ffa347',
    'ATM + 1 (PUT)': '#33e5ff',
    'ATM - 1 (CALL)': '#c99b06',
    'ATM - 1 (PUT)': '#0a76e1',
    'ATM + 2 (CALL)': '#b07300',
    'ATM + 2 (PUT)': '#102cbb',
    'ATM - 2 (CALL)': '#98560a',
    'ATM - 2 (PUT)': '#526e97'
}

export function findItmOption(arr: Strike[], x: number): Strike {
    let left = 0

    let right = arr.length - 1
    let closest = arr[0]
    let minDiff = Math.abs(x - closest.strike)
    while (left <= right) {
        const mid = Math.floor((left + right) / 2)
        const current = arr[mid]
        const currentDiff = Math.abs(x - current.strike)
        if (currentDiff < minDiff) {
            minDiff = currentDiff
            closest = current
        }
        if (current.strike === x) {
            return current
        } else if (current.strike < x) {
            left = mid + 1
        } else {
            right = mid - 1
        }
    }
    return closest
}

export function findTwoClosestOptions(arr: Strike[], x: number): Strike[] {
    const close = findItmOption(arr, x)
    const result = [close]
    const index = arr.indexOf(close)
    if (close.strike < x && arr.length > index + 1) {
        result.push(arr[index + 1])
    } else if (close.strike > x && index > 0) {
        result.push(arr[index - 1])
    }
    return result
}

export function getFiveStrikesBefore(arr: Strike[], comp: number): Strike[] {
    const beforeStrikes = arr.filter((strike) => strike.strike < comp)
    beforeStrikes.sort((a, b) => b.strike - a.strike)
    return beforeStrikes.slice(0, 5)
}

export function getFiveStrikesAfter(arr: Strike[], comp: number): Strike[] {
    const afterStrikes = arr.filter((strike) => strike.strike > comp)
    afterStrikes.sort()
    return afterStrikes.slice(0, 5)
}

export function isStrikeRoundingNeeded(strikes: number[], priceMultiplier: number | undefined): boolean {
    return (strikes.find((strike) => (Number(strike) % (priceMultiplier ?? 100) > 0)) === undefined)
}

export const calculateOptionOutlook = (ltpChange: number, oiChange: number): Outlook => {
    if (ltpChange > 0 && oiChange > 0) {
        return Outlook.LONG_BUILDUP
    }
    if (ltpChange < 0 && oiChange < 0) {
        return Outlook.LONG_UNWINDING
    }
    if (ltpChange > 0 && oiChange < 0) {
        return Outlook.SHORT_COVERING
    }
    if (ltpChange < 0 && oiChange > 0) {
        return Outlook.SHORT_BUILDUP
    }
    return Outlook.OUTLOOK_UNKNOWN
}

export const outlookToShortName = (outlook: Outlook): string => {
    switch (outlook) {
        case Outlook.LONG_BUILDUP:
            return 'LB'
        case Outlook.LONG_UNWINDING:
            return 'LU'
        case Outlook.SHORT_COVERING:
            return 'SC'
        case Outlook.SHORT_BUILDUP:
            return 'SB'
        default:
            return ''
    }
}



export const fetchOptionChainV2Initial = async (security: Security, dispatch: Dispatch<AnyAction>, sendFutures = true, expiries: number[] = [], optionExpiries: optionsTradesDataType[] | undefined = undefined) => {
    const payload: GetOptionChainRequestV2 = {
        security: {
            code: security?.code,
            exchange: security?.exchange
        },
        expiries,
        noOfStrikes: -1,
        requestedFields: [],
        timeBucket: {
            timeSeries: TimeBucket_TimeSeries.MINUTE,
            value: 1
        },
        sendFutures
    }
    return await fetchOptionChainV2(payload, dispatch, optionExpiries, 'both', false)
}

export const defaultTimebucket = {
    timeSeries: TimeBucket_TimeSeries.DAY,
    value: 1
}

export const fetchOptionChainV2 = async (payload: GetOptionChainRequestV2 | GetHistoricOptionChainRequest, dispatch: Dispatch<AnyAction>, optionExpiries: optionsTradesDataType[] | undefined = undefined, update: 'options' | 'futures' | 'both', resetExpandedRows: boolean, expandedRows?: string[], historicalTime?: number) => {
    const result: optionsTradesDataType[] = optionExpiries ? [...optionExpiries] : []
    let futuresDataFinal: futureTradeDataType[] = []
    const requestedToken = getSecurityKey(payload.security?.exchange ?? 0, payload.security?.code ?? '')
    try {
        const response = !historicalTime ? await getOptionChainV2(payload as GetOptionChainRequestV2) : await getHistoricalOptionChain(payload as GetHistoricOptionChainRequest)
        const responseToken = response.security ? getSecurityKey(response.security.exchange, response.security.code) : requestedToken
        if (['both', 'options'].includes(update)) {
            for (let i = 0; i < response.expiryOptionChain.length; i++) {
                const newTrades: optionTradeDataType[] = []
                let maxPain = 0
                const expiry = response.expiryOptionChain[i]
                if (optionExpiries && !expiry.strikeOptionChainLineItem.length) continue
                const expiryObj = getExpiryObjectFromTimestamp(expiry.expiry, expiry.weekly, historicalTime)
                const strikesArray = expiry.strikeOptionChainLineItem.map(strikeData => (({
                    strike: strikeData.strikePrice,
                    callOption: undefined,
                    putOption: undefined
                }) as Strike))

                const data: optionsTradesDataType = {
                    key: i.toString(),
                    expiration: {
                        expiry: expiryObj,
                        expirationDate: convertDateToShortEnglishFormat(expiryObj?.date ?? ''),
                        weeklyExpiry: expiry.weekly,
                        name: expiryObj?.name ?? ''
                    },
                    expirationDateKey: convertDateToShortEnglishFormat(expiryObj?.date ?? '', true),
                    numberOfDaysToExpiry: expiryObj.numberOfDaysToExpiry + 'd',
                    trades: newTrades,
                    expirySeconds: expiry.expiry.toString(),
                    loading: true,
                    strikes: strikesArray,
                    maxPain,
                    derived: false,
                    atmStrike: expiry?.atmStrike ?? 0,
                    ivxExpiry: expiry?.ivx ?? 0,
                    pcrExpiry: expiry?.pcr ?? 0,
                    standardDeviation: (expiry.standardDeviation ?? 0) * 100,
                    oneSd: 0,
                    minusOneSd: 0,
                    twoSd: 0,
                    minusTwoSd: 0,
                    timebucket: (result[i]?.timebucket ?? (payload as GetOptionChainRequestV2)?.timeBucket ?? defaultTimebucket) as TimeBucket
                }
                if (!optionExpiries || optionExpiries.length === 0) {
                    result.push(data)
                } else {
                    if (result[i]) {
                        result[i] = {
                            ...data,
                            trades: result[i].trades
                        }
                    } else {
                        result.push(data)
                    }
                }
            }

            (response.expiryOptionChain.filter(expiryData => expiryData.strikeOptionChainLineItem.length !== 0)).forEach(expiryData => {
                const expiry = expiryData.expiry
                const index = result.findIndex(r => r.expiration.expiry?.expiry === Number(expiry))
                if (expiryData && response.security && index !== -1) {

                    let newTrades: optionTradeDataType[] = []
                    let maxPain = 0
                    let maxPainValue = 0
                    if (expiryData.strikeOptionChainLineItem.length > 0) {
                        result[index].timebucket = (payload as GetOptionChainRequestV2)?.timeBucket as TimeBucket
                    }
                    const atmStrikeData = expiryData.strikeOptionChainLineItem.find(strikeData => strikeData.strikePrice === expiryData.atmStrike)
                    const atmPrice = expiryData.atmStrike + (((atmStrikeData?.callCandle?.topAsk??0)+(atmStrikeData?.callCandle?.topBid??0))/2) - (((atmStrikeData?.putCandle?.topAsk??0)+(atmStrikeData?.putCandle?.topBid??0))/2)
                    expiryData.strikeOptionChainLineItem.forEach((strikeData) => {
                        const s = strikeData.strikePrice
                        if ((strikeData.callCandle?.openInterest ?? 0) + (strikeData.putCandle?.openInterest ?? 0) > maxPainValue) {
                            maxPain = Number(s)
                            maxPainValue = (strikeData.callCandle?.openInterest ?? 0) + (strikeData.putCandle?.openInterest ?? 0)
                        }
                        const callOption = strikeData.callCandle
                        const putOption = strikeData.putCandle
                        const callSecurity: Security = {
                            exchange: response.security?.exchange ?? 0,
                            code: expiryData.securityCodePrefix + s + '_CE',
                            strikePrice: Number(s),
                            expiry: {
                                expiry: Number(expiry),
                                expiryDate: convertDateToShortEnglishFormat(expiry.toString(), true)
                            } as any,
                            optionType: OptionType.CE,
                            priceMultiplier: response.priceMultiplier,
                            volumeFreezeQuantity: expiryData.volumeFreezeQuantity,
                            lotSize: expiryData.lotSize,
                            tickSize: expiryData.tickSize,
                            underlyingSecurityCode: response.security?.code,
                            categoryCode: SecurityCategory_CategoryCode.OPTIONS
                        }
                        const putSecurity: Security = {
                            exchange: response.security?.exchange ?? 0,
                            code: expiryData.securityCodePrefix + s + '_PE',
                            strikePrice: Number(s),
                            expiry: {
                                expiry: Number(expiry),
                                expiryDate: convertDateToShortEnglishFormat(expiry.toString(), true)
                            } as any,
                            optionType: OptionType.PE,
                            priceMultiplier: response.priceMultiplier,
                            volumeFreezeQuantity: expiryData.volumeFreezeQuantity,
                            lotSize: expiryData.lotSize,
                            tickSize: expiryData.tickSize,
                            underlyingSecurityCode: response.security?.code,
                            categoryCode: SecurityCategory_CategoryCode.OPTIONS
                        }
                        const callLTPChange = (callOption?.candleChange?.currentLtp ?? 0) - (callOption?.candleChange?.previousLtp ?? 0)
                        const callOiChange = (callOption?.candleChange?.currentOi ?? 0) - (callOption?.candleChange?.previousOi ?? 0)
                        const putLTPChange = (putOption?.candleChange?.currentLtp ?? 0) - (putOption?.candleChange?.previousLtp ?? 0)
                        const putOiChange = (putOption?.candleChange?.currentOi ?? 0) - (putOption?.candleChange?.previousOi ?? 0)

                        const callOptionData = {
                            ...callOption,
                            ltp: callOption?.candleChange?.currentLtp,
                            ltpChange: callLTPChange,
                            ltpChangePercent: ((callLTPChange) / (callOption?.candleChange?.previousLtp ?? 1)) * 100,
                            security: callSecurity,
                            openInterest: callOption?.openInterest ?? 0,
                            oiChanges: {
                                outlook: calculateOptionOutlook(callLTPChange, callOiChange),
                                oiChange: {
                                    timeBucket: (payload as GetOptionChainRequestV2)?.timeBucket ?? defaultTimebucket,
                                    value: callOption?.openInterest ?? 0,
                                    change: callOiChange,
                                    changePercent: ((callOiChange) / (callOption?.candleChange?.previousOi ?? 1)) * 100
                                }
                            },
                            illiquid: false,
                            spanMargin: callOption?.spanMargin,
                            exposureMargin: callOption?.exposureMargin
                        } as CustomOptionSecurityType | undefined

                        const putOptionData = {
                            ...putOption,
                            ltp: putOption?.candleChange?.currentLtp,
                            ltpChange: putLTPChange,
                            ltpChangePercent: ((putLTPChange) / (putOption?.candleChange?.previousLtp ?? 1)) * 100,
                            security: putSecurity,
                            openInterest: putOption?.openInterest ?? 0,
                            oiChanges: {
                                outlook: calculateOptionOutlook(putLTPChange, putOiChange),
                                oiChange: {
                                    timeBucket: (payload as GetOptionChainRequestV2)?.timeBucket ?? defaultTimebucket,
                                    value: putOption?.openInterest ?? 0,
                                    change: putOiChange,
                                    changePercent: ((putOiChange) / (putOption?.candleChange?.previousOi ?? 1)) * 100
                                }
                            },
                            illiquid: false,
                            spanMargin: putOption?.spanMargin,
                            exposureMargin: putOption?.exposureMargin
                        } as CustomOptionSecurityType | undefined

                        const finalCallCandle = strikeData.callCandle ? callOptionData : undefined
                        const finalPutCandle = strikeData.putCandle ? putOptionData : undefined

                        newTrades.push({
                            key: callSecurity.code ?? '',
                            timeStamp: response.referenceTimestamp,
                            callOption: finalCallCandle,
                            putOption: finalPutCandle,
                            itmOption: Number(s) < (atmPrice ?? expiryData.atmStrike) ? finalCallCandle : finalPutCandle,
                            otmOption: Number(s) < (atmPrice ?? expiryData.atmStrike) ? finalPutCandle : finalCallCandle,
                            strikeCol: {
                                strike: Number(s) ?? 0,
                                pcr: strikeData.pcr,
                                pcrStrength: 0,
                                numberOfDaysToExpiry: result[index].numberOfDaysToExpiry
                            },
                            expiry: result[index].expiration.name
                        })
                    })
                    newTrades = calculateOiRanks(newTrades, expiryData.atmStrike)
                    result[index].trades = newTrades
                    // result[index].expiration.expiry = res.optionChain?.expiry
                    result[index].maxPain = maxPain
                    result[index].loading = false
                    result[index].standardDeviation = (expiryData.standardDeviation ?? 0) * 100
                    const atmStrike = expiryData.atmStrike
                    const oneSdPercent = (expiryData.standardDeviation ?? 0) * atmStrike
                    result[index].oneSd = atmStrike + oneSdPercent
                    result[index].minusOneSd = atmStrike - oneSdPercent
                    result[index].twoSd = atmStrike + 2 * oneSdPercent
                    result[index].minusTwoSd = atmStrike - 2 * oneSdPercent

                    const found = result[index].trades.find((t) => t.strikeCol.strike === maxPain)
                    if (found) {
                        found.strikeCol.isMaxPain = true
                        result[index].trades.forEach((t) => {
                            t.strikeCol.maxPain = maxPain
                        })
                    }
                }
            })
            if (result) {
                setOptionChainDataFunction({
                    token: responseToken,
                    tradeData: result,
                    initialLoad: true,
                    underlyingPrice: {
                        underlyingCurrentPrice: response.underlyingCurrentPrice,
                        underlyingLastDayClosingPrice: response.underlyingLastDayClosingPrice
                    },
                    opitonChainViewKey: !historicalTime ? OptionChainViewKey.LIVE : OptionChainViewKey.HISTORICAL
                })
                const expandedRowsNew: string[] = []
                if (expandedRows?.length) {
                    expandedRows.forEach(row => {
                        if (result[Number(row)]?.trades?.length) {
                            expandedRowsNew.push(row)
                        }
                    })
                }
                if (resetExpandedRows) {
                    (response.expiryOptionChain.filter(expiryData => expiryData.strikeOptionChainLineItem.length !== 0)).forEach(exp => {
                        const index = result.findIndex(r => Number(r.expirySeconds) === exp.expiry)
                        if (index !== -1 && !expandedRowsNew.includes(index.toString())) {
                            expandedRowsNew.push(index.toString())
                        }
                    })
                    if (historicalTime) {
                        dispatch(setOptionChainExpandedRows({
                            expandedRows: (expandedRows && expandedRows?.length > 0) ? expandedRows : ['0'],
                            optionChainViewKey: OptionChainViewKey.HISTORICAL
                        }))
                    } else {
                        dispatch(setOptionChainExpandedRows({
                            expandedRows: expandedRowsNew.length > 0 ? expandedRowsNew : ['0'],
                            optionChainViewKey: OptionChainViewKey.LIVE
                        }))
                    }
                }
            }
        }
        if (['both', 'futures'].includes(update)) {
            const futuresData: futureTradeDataType[] = []
            for (let i = 0; i < response.expiryFuture.length; i++) {
                const value = response.expiryFuture[i];
                if (!value.security || !value.futureCandle) continue
                const secKey = getSecurityKey(value.security?.exchange ?? 0, value.security?.code ?? '')
                const fullSecurity: Security = {
                    exchange: value.security?.exchange,
                    code: value.security?.code,
                    strikePrice: 0,
                    expiry: {
                        expiry: value.expiry,
                        expiryDate: convertDateToShortEnglishFormat(value.expiry.toString(), true)
                    } as any,
                    priceMultiplier: response.priceMultiplier,
                    volumeFreezeQuantity: value.volumeFreezeQuantity,
                    lotSize: value.lotSize,
                    tickSize: value.tickSize,
                    underlyingSecurityCode: response.security?.code
                }
                futuresData.push({
                    expiry: value.expiry,
                    numberOfDaysToExpiry: `${Math.ceil(calculateDifferenceInDays(value.expiry.toString(), new Date().setHours(0, 0, 0, 0).toString())).toFixed(0)}d`,
                    security: value.security,
                    bid: value.futureCandle.topBid,
                    timestamp: response.referenceTimestamp,
                    ask: value.futureCandle.topAsk,
                    volume: value.futureCandle.volume,
                    derived: fullSecurity.derived,
                    openInterest: value.futureCandle.openInterest,
                    impliedVolatility: value.futureCandle.impliedVolatility,
                    key: secKey,
                    ltp: value.futureCandle.candleChange?.currentLtp ?? 0,
                    ltpChange: (value.futureCandle.candleChange?.currentLtp ?? 0) - (value.futureCandle.candleChange?.previousLtp ?? 0),
                    lptChangePercent: 100 * (((value.futureCandle.candleChange?.currentLtp ?? 0) - (value.futureCandle.candleChange?.previousLtp ?? 0)) / ((value.futureCandle.candleChange?.previousLtp ?? 1))),
                    oiChange: (value.futureCandle.candleChange?.currentOi ?? 0) - (value.futureCandle.candleChange?.previousOi ?? 0),
                    candleChange: value.futureCandle.candleChange
                } as futureTradeDataType)
            }
            if (((payload as GetOptionChainRequestV2)?.sendFutures ?? true) && futuresData.length) {
                futuresDataFinal = futuresData.sort((a, b) => a.expiry - b.expiry)
                dispatch(setFutureExpiriesData({
                    token: getSecurityKey(response.security?.exchange ?? 0, response.security?.code ?? ''),
                    data: futuresDataFinal
                }))
            }

        }

    } catch (error) {
        console.error('Error fetching option chain', error)
        const optionsState = store.getState().optionsStore as OptionsStateType
        if (['both', 'options'].includes(update) && !(requestedToken in optionsState.tradeData)) {
            setOptionChainDataFunction({
                token: requestedToken,
                tradeData: [],
                initialLoad: true,
                underlyingPrice: { underlyingCurrentPrice: 0 },
                opitonChainViewKey: !historicalTime ? OptionChainViewKey.LIVE : OptionChainViewKey.HISTORICAL
            })
        }
    }
    return {
        optionsData: result,
        futuresData: futuresDataFinal
    }
}

export const fetchOptionExpiriesV2 = async (underlying: Partial<Security>, dispatch: Dispatch<AnyAction>): Promise<DropDownExpiries[]> => {
    const dropdownExpiries: DropDownExpiries[] = []
    try {
        const response = await getOptionExpiriesV2({
            exchange: underlying.exchange ?? 0,
            securityCode: underlying.code ?? ''
        })
        const optionExpiries = response

        const underlyingSec = optionExpiries.security
        if (!underlyingSec) return dropdownExpiries
        optionExpiries.expiryStrikes.forEach((expiryData) => {
            if (expiryData.expiry) {
                if (expiryData.derived) {
                    dropdownExpiries.push({
                        expiry: expiryData.expiry,
                        derived: expiryData.derived,
                        strikes: [{
                            strike: 0,
                            callSecurity: {
                                exchange: underlyingSec.exchange,
                                code: expiryData.securityCodePrefix + 'CE'
                            },
                            putSecurity: {
                                exchange: underlyingSec.exchange,
                                code: expiryData.securityCodePrefix + 'PE'
                            }
                        }],
                        strikesArray: [0]
                    })
                } else {
                    const data: DropDownExpiries = {
                        expiry: expiryData.expiry,
                        derived: expiryData.derived,
                        strikes: expiryData.strikes.map(strike => ({
                            strike: strike,
                            callSecurity: {
                                exchange: underlyingSec.exchange,
                                code: expiryData.securityCodePrefix + strike + '_CE'
                            },
                            putSecurity: {
                                exchange: underlyingSec.exchange,
                                code: expiryData.securityCodePrefix + strike + '_PE'
                            }
                        })),
                        strikesArray: expiryData.strikes
                    }
                    dropdownExpiries.push(data)
                }
            }
        })

        if (response) {
            dispatch(setDropdownOptionExpiries({
                token: getSecurityKey(underlyingSec.exchange ?? 0, underlyingSec.code ?? ''),
                dropdownExpiries: dropdownExpiries
            }))
        }
    } catch (error) {
        console.error('Error fetching option expiries', error)
    }
    return dropdownExpiries
}

export const getIvStrikes = () => {
    let ivStrikes: ATMsStrikeMenuItemType[] = []
    ivStrikes.push({
        label: 'ATM',
        value: 0
    })
    for (let i = 1; i <= 2; i++) {
        const lbl = `ATM +/- ${i}`
        ivStrikes.push({
            label: lbl,
            value: i
        })
    }
    return ivStrikes
}

export const findAtmStrike = (price: number, strikes: Strike[]): number => {
    let l = 0, r = strikes.length
    while (r - l > 1) {
        const mid = Math.floor((r + l) / 2)
        if (strikes[mid].strike === price) return price
        if (strikes[mid].strike < price) {
            l = mid
        } else {
            r = mid
        }
    }
    return strikes[r].strike
}

export const findSimulatorSquareOffOrder = (order: OrdersDataType, orders: OrdersDataType[], timeStamp: number) => {
    if (!order.sqOffByOrderId) return undefined
    const sqOffOrder = orders.find(o => o.uuid === order.sqOffByOrderId)
    if ((sqOffOrder?.timeStamp ?? 0) > timeStamp) return undefined
    return sqOffOrder
}